import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import NavBar from '../components/NavBar';
import { useLanguage } from '../i18n/LanguageContext';

const Messages = ({ user, onLogout }) => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const recipientId = searchParams.get('to') || null;
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/messages`);
      setMessages(res.data);
      // Mark all as read
      await axios.put(`${API}/messages/read-all`);
    } catch {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages`, {
        content: newMessage,
        to_user_id: recipientId
      });
      setNewMessage('');
      fetchMessages();
      toast.success(recipientId ? 'Message sent to caregiver' : 'Message sent');
    } catch {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diffH = (now - d) / 3600000;
    if (diffH < 1) return `${Math.round(diffH * 60)}m ago`;
    if (diffH < 24) return `${Math.round(diffH)}h ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  return (
    <div className="dashboard-container" data-testid="messages-page">
      <NavBar onLogout={onLogout} />
      <div className="dashboard-content page-container">
        <h1 className="page-title" data-testid="messages-title">
          <i className="fa-solid fa-envelope" style={{ marginRight: '10px', color: '#2c5364' }}></i>Messages
        </h1>

        {/* Send Message */}
        <div className="section-card" style={{ marginBottom: '1.5rem' }} data-testid="send-message-section">
          <h3 style={{ color: '#2c5364', marginBottom: '0.75rem' }}>
            <i className="fa-solid fa-paper-plane" style={{ marginRight: '8px' }}></i>
            {recipientId ? 'Message Caregiver' : 'Send a Message'}
          </h3>
          {recipientId && (
            <p style={{ fontSize: '0.85rem', color: '#6b8899', marginTop: 0, marginBottom: '0.75rem' }} data-testid="message-recipient-hint">
              <i className="fa-solid fa-user-check" style={{ marginRight: '6px', color: '#16a34a' }}></i>
              Sending directly to caregiver from the directory.
            </p>
          )}
          <form onSubmit={handleSend} style={{ display: 'flex', gap: '0.75rem' }}>
            <textarea
              value={newMessage}
              onChange={e => setNewMessage(e.target.value)}
              placeholder={recipientId ? 'Say hello to your fellow caregiver…' : 'Type your message to the CareGuard team...'}
              rows={2}
              required
              style={{ flex: 1, padding: '0.75rem', borderRadius: '8px', border: '2px solid #e2e8f0', resize: 'vertical', fontSize: '0.95rem' }}
              data-testid="message-input"
            />
            <button type="submit" className="submit-button" disabled={sending}
              style={{ width: 'auto', padding: '0.75rem 1.25rem', alignSelf: 'flex-end' }}
              data-testid="send-message-btn">
              <i className="fa-solid fa-paper-plane"></i> {sending ? 'Sending...' : 'Send'}
            </button>
          </form>
        </div>

        {/* Message List */}
        <div className="section-card" data-testid="messages-list">
          <h3 style={{ color: '#2c5364', marginBottom: '1rem' }}>
            <i className="fa-solid fa-inbox" style={{ marginRight: '8px' }}></i>
            Inbox ({messages.length})
          </h3>
          {loading ? (
            <p>{t('common.loading')}</p>
          ) : messages.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div style={{ fontSize: '2.5rem', color: '#94a3b8', marginBottom: '0.75rem' }}><i className="fa-solid fa-envelope-open"></i></div>
              <h3 style={{ color: '#6b8899' }}>No Messages Yet</h3>
              <p style={{ color: '#94a3b8' }}>Messages from the CareGuard team will appear here.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {messages.map(msg => {
                const isFromMe = msg.from_user_id === user.id;
                const isAdmin = msg.from_role === 'admin';
                return (
                  <div key={msg.id} style={{
                    padding: '0.75rem 1rem', borderRadius: '8px',
                    background: isFromMe ? '#e7f3ff' : '#f8f9fa',
                    borderLeft: `4px solid ${isAdmin ? '#2c5364' : isFromMe ? '#3b82f6' : '#16a34a'}`,
                    opacity: msg.read ? 1 : 1
                  }} data-testid={`message-${msg.id}`}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontWeight: '600', color: '#2c5364', fontSize: '0.9rem' }}>
                          {isFromMe ? 'You' : msg.from_name}
                        </span>
                        {isAdmin && !isFromMe && (
                          <span style={{ padding: '1px 6px', borderRadius: '4px', background: '#2c5364', color: '#fff', fontSize: '0.65rem', fontWeight: '700' }}>ADMIN</span>
                        )}
                        {!msg.read && !isFromMe && (
                          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#3b82f6', display: 'inline-block' }}></span>
                        )}
                      </div>
                      <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{formatDate(msg.created_at)}</span>
                    </div>
                    <p style={{ margin: 0, color: '#374151', fontSize: '0.9rem', lineHeight: '1.5' }}>{msg.content}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Messages;
